import { SimpleMuiButton } from '@dreamstack/feature-components/src/layout/SimpleMuiButton'
import { useTranslation } from '@dreamstack/i18n'
import type { FunctionComponent } from 'react'
import { forwardRef, useState } from 'react'
import type { IndexType } from '..'
import { Autocomplete } from './Autocomplete'
import { useSearchContext } from './SearchContext'
import { useAutocompletePlugins } from './useAutocompletePlugins'

const SEARCH_BUTTON_WIDTH = 120

// Converted from styled.div + tw to plain functional component, now ref-forwarding
const AutocompleteWrapper = forwardRef<
  HTMLDivElement,
  { children?: React.ReactNode }
>(({ children }, ref) => {
  return (
    <div
      ref={ref}
      className="flex-1 [&_.aa-Form]:h-[56px] [&_.aa-Form]:bg-accentroGray-50! [&_.aa-Form]:text-accentroGray-full! [&_.aa-Form]:border-0! [&_.aa-Form]:rounded-none! [&_.aa-Input]:px-px-16! [&_.aa-Input]:ml:px-px-24! [&_.aa-Input]:overflow-ellipsis! [&_.aa-InputWrapperPrefix]:hidden! [&_.aa-InputWrapperSuffix]:hidden!"
      style={{ ['--aa-primary-color-rgb' as any]: '80, 128, 144' }}
    >
      {children}
    </div>
  )
})
AutocompleteWrapper.displayName = 'AutocompleteWrapper'

export const SearchAutocomplete: FunctionComponent<
  React.PropsWithChildren<{ indexType: IndexType }>
> = ({ indexType }) => {
  const {
    searchState,
    setSearchState,
    submitState,
    resetState,
    autocompleteRef,
  } = useSearchContext()

  const plugins = useAutocompletePlugins({
    indexType: indexType,
    setSearchState,
  })

  const t = useTranslation()
  const [currentQuery, setCurrentQuery] = useState('')

  return (
    <>
      <div className="flex flex-col space-y-px-16 sm:flex-row sm:space-y-0 sm:space-x-px-16">
        <Autocomplete
          autocompleteRef={autocompleteRef}
          placeholder={t('accentro:newsSearch.searchPlaceholder')}
          detachedMediaQuery="none"
          initialState={{
            query: searchState.query,
          }}
          openOnFocus={true}
          onSubmit={submitState}
          onReset={resetState}
          plugins={plugins}
          onStateChange={({ state }) => setCurrentQuery(state.query)}
          getPanelProps={({ props }) => ({
            ...props,
            style: /*css*/ `
              z-index: 1000;
              margin-right: -${SEARCH_BUTTON_WIDTH + 6}px;
              --aa-primary-color-rgb: 80, 128, 144;
              --aa-icon-color-rgb: 43, 45, 54;
              --aa-icon-color-alpha: 1;
              /* default: --aa-muted-color-rgb: 128,126,163; --aa-muted-color-alpha: 0.6; */
              --aa-muted-color-rgb: 80, 128, 144;
              --aa-muted-color-alpha: 0.6;
              --aa-panel-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.25);
              border-radius: 0;
            `,
          })}
          // Pass new wrapper instead of styled component
          StyledDiv={AutocompleteWrapper}
        />
        <SimpleMuiButton
          variant={indexType === 'news' ? 'contained' : 'outlined'}
          color={indexType === 'news' ? 'primary' : 'inherit'}
          onClick={() => {
            submitState({ state: { query: currentQuery } })
          }}
          style={{ width: SEARCH_BUTTON_WIDTH }}
          size="small"
        >
          {t('accentro:newsSearch.searchAction')}
        </SimpleMuiButton>
      </div>
    </>
  )
}
